import React from 'react';
import Close from '@mui/icons-material/Close';
import classNames from 'classnames';
import useStyles from 'src/styles/di-theme';
import LoadingIndicator from 'src/lib/components/LoadingIndicator';
import Shimmer from 'src/components/Shimmer';
import { TagRenderer } from './Renderer';
import useDetectionStyles from './styles/detection';
import {
	Box,
	Typography,
	IconButton,
	Drawer,
	BDSProvider,
} from '@barracuda-internal/bds-core-v2';
import ScanLogPopupV2 from './ScanLogPopupV2';
import DetectionDetailSections from './DetectionDetailSections';

const shimmerWidth = [100, 90, 70, 50, 80, 60, 30];

interface DetectionDrawerProps {
	open: boolean;
	onClose: () => void;
	onExpand: () => void;
	feed: any;
	accessToken: string;
	timeStamp: number;
	showShimmer: boolean;
	setShowShimmer: (show: boolean) => void;
	imageSpecs: any;
	generatePreviewWidthAndHeight: (
		width: number,
		height: number,
		id: string,
	) => void;
	selectedClassifer: string;
	pickFinding: (
		classifier: string,
		idx: number,
		zoom: number,
		visiblePreviews: any[],
	) => void;
	zoom: number;
	visiblePreviews: any[];
	classifierTags: any[];
	isDrawerLoading: boolean;
	setSearch: (search: string) => void;
	esFeed: any;
	setShowInternalUsers: (show: boolean) => void;
	setShowExternalUsers: (show: boolean) => void;
	showExternalUsers: boolean;
	handleCopyClick: () => void;
	tagsWithReport: any[];
	showInternalUsers: boolean;
}

const DetectionDrawer: React.FC<DetectionDrawerProps> = ({
	open,
	onClose,
	onExpand,
	feed,
	accessToken,
	timeStamp,
	showShimmer,
	setShowShimmer,
	imageSpecs,
	generatePreviewWidthAndHeight,
	selectedClassifer,
	pickFinding,
	zoom,
	visiblePreviews,
	classifierTags,
	isDrawerLoading,
	setSearch,
	esFeed,
	setShowInternalUsers,
	setShowExternalUsers,
	showExternalUsers,
	handleCopyClick,
	tagsWithReport,
	showInternalUsers,
}) => {
	const classes = useStyles();
	const customClasses = useDetectionStyles();
	const [showScanlog, setShowScanlog] = React.useState(false);

	const handleImageClick = () => {
		onExpand();
		pickFinding(selectedClassifer, 1, zoom, visiblePreviews);
	};

	return (
		<BDSProvider>
			<Drawer
				anchor="right"
				open={open}
				onClose={onClose}
				variant="temporary"
			>
				<ScanLogPopupV2
					showScanlog={showScanlog}
					searchText={esFeed.fileName}
					itemId={esFeed.itemId}
					closePopup={() => setShowScanlog(false)}
				/>
				<Box
					sx={{
						width: 416,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: 2,
						}}
						className={classNames(
							classes['p_2'],
							classes['border_bottom_gray'],
						)}
					>
						<Typography
							variant="h4"
							style={{
								textOverflow: 'ellipsis',
								overflow: 'hidden',
							}}
							color="textPrimary"
						>
							{esFeed.fileName}
						</Typography>
						<IconButton onClick={onClose}>
							<Close />
						</IconButton>
					</Box>

					<Box
						sx={{
							padding: '16px',
							overflow: 'auto',
						}}
					>
						{isDrawerLoading && (
							<Box
								className={
									customClasses[
										'detection-sidePanel-loading-container'
									]
								}
							>
								<LoadingIndicator message="Loading ..." />
							</Box>
						)}
						{!isDrawerLoading && (
							<>
								<section
									className={classNames(
										classes['ptb_2'],
										classes['overflow_hidden'],
									)}
								>
									{accessToken !== '' ? (
										feed.previews.length > 0 &&
										classifierTags.length > 0 ? (
											<Box
												className={
													customClasses['pos-rel']
												}
											>
												{showShimmer &&
													!imageSpecs[
														feed.previews[0].id
													] && (
														<Box
															className={
																customClasses[
																	'feed-side-preview-shimmer'
																]
															}
														>
															<Shimmer
																shimmerWidth={
																	shimmerWidth
																}
																skeletonHeight={
																	15
																}
																animation="wave"
																style={{
																	marginBottom: 6,
																	marginTop: 6,
																}}
															/>
														</Box>
													)}
												<img
													onClick={handleImageClick}
													className={classNames(
														classes['h_200'],
														classes['image_center'],
														classes[
															'cursor_pointer'
														],
													)}
													src={`${feed.previews[0].url}?access_token=${accessToken}&t=${timeStamp}`}
													onLoad={(event) => {
														const image =
															event.target as HTMLImageElement;
														setShowShimmer(false);
														generatePreviewWidthAndHeight(
															image.naturalWidth,
															image.naturalHeight,
															feed.previews[0].id,
														);
													}}
												/>
											</Box>
										) : (
											<Typography
												variant="h4"
												color="textPrimary"
												className={
													classes['text_center']
												}
											>
												No Preview Available
											</Typography>
										)
									) : (
										<LoadingIndicator message="Loading this preview" />
									)}
								</section>
								<section
									className={classNames(classes['ptb_2'])}
								>
									<Typography
										variant="h4"
										color="textPrimary"
									>
										Violations
									</Typography>
									<Typography
										variant="body2"
										color="textSecondary"
										sx={{ fontSize: '12px' }}
									>
										Occurences of sensitive information in
										this shared file
									</Typography>
									<Box
										className={classNames(classes['ptb_2'])}
									>
										<TagRenderer
											dataItem={feed}
										></TagRenderer>
									</Box>
								</section>

								<DetectionDetailSections
									esFeed={esFeed}
									feed={feed}
									setSearch={setSearch}
									handleCopyClick={handleCopyClick}
									onViewScanLog={() => setShowScanlog(true)}
									showInternalUsers={showInternalUsers}
									setShowInternalUsers={setShowInternalUsers}
									showExternalUsers={showExternalUsers}
									setShowExternalUsers={setShowExternalUsers}
									tagsWithReport={tagsWithReport}
								/>
							</>
						)}
					</Box>
				</Box>
			</Drawer>
		</BDSProvider>
	);
};

export default DetectionDrawer;
